import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, CalendarDays, CheckCircle2, History, RefreshCw, Settings2, Share2, Sparkles } from 'lucide-react'
import Layout from '../../components/Layout'
import Seo from '../../components/Seo'
import { socialStudioService } from '../../services/socialStudioService'
import SocialPostCard from './SocialPostCard'
import SocialPostEditor from './SocialPostEditor'
import SocialVisual from './SocialVisual'

const localDateKey = (date = new Date()) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const tabItems = [
  { key: 'today', label: 'Dnešní fronta', icon: CalendarDays },
  { key: 'history', label: 'Historie', icon: History },
  { key: 'settings', label: 'Nastavení', icon: Settings2 },
]

const statusLabel = {
  draft: 'Koncept',
  ready: 'Připraveno',
  posted: 'Publikováno',
  skipped: 'Přeskočeno',
}

const SettingsPanel = ({ settings, onSave }) => {
  const [form, setForm] = useState(settings)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => setForm(settings), [settings])

  const updateSchedule = (key, value) => setForm((current) => ({
    ...current,
    schedule: { ...current.schedule, [key]: value },
  }))

  const submit = async (event) => {
    event.preventDefault()
    try {
      setSaving(true)
      setError('')
      await onSave({
        schedule: form.schedule,
        focusRegion: form.focusRegion,
        tone: form.tone,
        minimumConfidence: Number(form.minimumConfidence),
        reuseAfterDays: Number(form.reuseAfterDays),
      })
    } catch (requestError) {
      setError(requestError.message || 'Nastavení se nepodařilo uložit.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={submit} className="max-w-4xl rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <div className="flex items-start gap-3">
        <span className="rounded-xl bg-violet-50 p-2.5 text-violet-700"><Settings2 className="h-5 w-5" /></span>
        <div>
          <h2 className="text-lg font-semibold text-slate-950">Redakční nastavení</h2>
          <p className="mt-1 text-sm text-slate-500">Časy určují pořadí přípravy. Social Studio nic automaticky nepublikuje.</p>
        </div>
      </div>

      {error && <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        {[
          ['morning', 'Ranní příležitost'],
          ['midday', 'Polední trh'],
          ['evening', 'Večerní wow'],
        ].map(([key, label]) => (
          <label key={key} className="text-sm font-medium text-slate-700">
            {label}
            <input type="time" value={form.schedule?.[key] || ''} onChange={(event) => updateSchedule(key, event.target.value)} className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
          </label>
        ))}
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <label className="text-sm font-medium text-slate-700">
          Oblast pro tržní přehled
          <input value={form.focusRegion || ''} maxLength={80} onChange={(event) => setForm((current) => ({ ...current, focusRegion: event.target.value }))} className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
        </label>
        <label className="text-sm font-medium text-slate-700">
          Tón
          <select value={form.tone || 'expert_friendly'} onChange={(event) => setForm((current) => ({ ...current, tone: event.target.value }))} className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100">
            <option value="expert_friendly">Odborný a přístupný</option>
            <option value="analytical">Analytický</option>
            <option value="playful">Lehce hravý</option>
          </select>
        </label>
        <label className="text-sm font-medium text-slate-700">
          Minimální důvěra odhadu
          <input type="number" min="0.3" max="0.95" step="0.05" value={form.minimumConfidence ?? 0.55} onChange={(event) => setForm((current) => ({ ...current, minimumConfidence: event.target.value }))} className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
        </label>
        <label className="text-sm font-medium text-slate-700">
          Stejnou nemovitost znovu nejdříve za
          <div className="mt-2 flex items-center gap-2">
            <input type="number" min="7" max="365" value={form.reuseAfterDays ?? 45} onChange={(event) => setForm((current) => ({ ...current, reuseAfterDays: event.target.value }))} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
            <span className="text-sm text-slate-500">dní</span>
          </div>
        </label>
      </div>

      <button type="submit" disabled={saving} className="mt-6 rounded-xl bg-violet-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-800 disabled:opacity-50">
        {saving ? 'Ukládám…' : 'Uložit nastavení'}
      </button>
    </form>
  )
}

const HistoryPanel = ({ posts, loading }) => {
  if (loading) return <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Načítám historii…</div>
  if (!posts.length) return <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">Historie je zatím prázdná. Schválené, publikované a přeskočené návrhy se objeví zde.</div>
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {posts.map((post) => (
        <article key={post._id} className="grid grid-cols-[120px_minmax(0,1fr)] gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <SocialVisual post={post} compact />
          <div className="min-w-0 py-1">
            <div className="text-xs font-semibold uppercase tracking-wide text-violet-700">{new Date(`${post.slotDate}T12:00:00`).toLocaleDateString('cs-CZ')} · {post.scheduledTime}</div>
            <h3 className="mt-2 line-clamp-2 font-semibold text-slate-950">{post.draft?.headline}</h3>
            <p className="mt-2 text-xs leading-5 text-slate-500">{post.evidence?.selectionReason}</p>
            <span className="mt-3 inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">{statusLabel[post.status] || post.status}</span>
          </div>
        </article>
      ))}
    </div>
  )
}

const SocialStudio = () => {
  const [activeTab, setActiveTab] = useState('today')
  const [date, setDate] = useState(localDateKey)
  const [posts, setPosts] = useState([])
  const [settings, setSettings] = useState(null)
  const [historyPosts, setHistoryPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [busyById, setBusyById] = useState({})
  const [selectedPost, setSelectedPost] = useState(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const loadToday = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const payload = await socialStudioService.getToday(date)
      setPosts(payload.posts || [])
      setSettings(payload.settings)
    } catch (requestError) {
      console.error('Unable to load Social Studio:', requestError)
      setError(requestError.message || 'Social Studio se nepodařilo načíst.')
    } finally {
      setLoading(false)
    }
  }, [date])

  useEffect(() => { loadToday() }, [loadToday])

  useEffect(() => {
    if (activeTab !== 'history') return
    let active = true
    setHistoryLoading(true)
    socialStudioService.getHistory()
      .then((payload) => { if (active) setHistoryPosts(payload.posts || []) })
      .catch((requestError) => { if (active) setError(requestError.message || 'Historii se nepodařilo načíst.') })
      .finally(() => { if (active) setHistoryLoading(false) })
    return () => { active = false }
  }, [activeTab])

  useEffect(() => {
    if (!notice) return undefined
    const timeout = window.setTimeout(() => setNotice(''), 2400)
    return () => window.clearTimeout(timeout)
  }, [notice])

  const readyCount = useMemo(() => posts.filter((post) => post.status === 'ready').length, [posts])
  const postedCount = useMemo(() => posts.filter((post) => post.status === 'posted').length, [posts])

  const updatePost = (updated) => {
    setPosts((current) => current.map((post) => post._id === updated._id ? updated : post))
    setSelectedPost((current) => current?._id === updated._id ? updated : current)
  }

  const copyText = async (text, message) => {
    try {
      await navigator.clipboard.writeText(text || '')
      setNotice(message)
    } catch (_error) {
      setError('Text se nepodařilo zkopírovat do schránky.')
    }
  }

  const runPostOperation = async (post, operation, callback) => {
    try {
      setBusyById((current) => ({ ...current, [post._id]: operation }))
      setError('')
      updatePost(await callback())
    } catch (requestError) {
      setError(requestError.message || 'Operace se nezdařila.')
    } finally {
      setBusyById((current) => ({ ...current, [post._id]: null }))
    }
  }

  const handleReplace = (post) => runPostOperation(post, 'replace', () => socialStudioService.replacePost(post._id))
  const handleStatus = (post, status) => runPostOperation(post, status, () => socialStudioService.updateStatus(post._id, status, post.draft.revision))

  const handleSettingsSave = async (payload) => {
    const saved = await socialStudioService.saveSettings(payload)
    setSettings((current) => ({ ...current, ...saved }))
    setNotice('Nastavení uloženo. Nové časy se použijí u nově připravených dnů.')
  }

  const headerContent = (
    <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 className="text-xl font-semibold text-slate-950 sm:text-2xl">Social Studio</h1>
        <p className="text-sm text-slate-500">Příprava příspěvků pro Instagram a X bez automatického publikování.</p>
      </div>
      <div className="flex items-center gap-2">
        <input type="date" value={date} onChange={(event) => setDate(event.target.value)} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-violet-500" />
        <button type="button" onClick={loadToday} disabled={loading} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Obnovit
        </button>
      </div>
    </div>
  )

  return (
    <Layout headerContent={headerContent}>
      <Seo title="Social Studio" description="Příprava sociálního obsahu Realitního Jestřába." canonicalUrl={typeof window !== 'undefined' ? window.location.href : undefined} />
      <div className="mr-auto max-w-7xl space-y-5 px-3 py-6 sm:px-6">
        <section className="overflow-hidden rounded-2xl bg-slate-950 p-5 text-white shadow-sm sm:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3">
              <span className="rounded-xl bg-white/10 p-2.5 text-lime-300"><Share2 className="h-5 w-5" /></span>
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-lime-300">Redakční den</div>
                <h2 className="mt-2 text-xl font-semibold">Tři hotové nápady, jeden lidský souhlas</h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">Kandidáti vycházejí z aktivních nabídek, cenové historie, odhadů a ověřených tržních souhrnů. Každý text můžete upravit, stáhnout a ručně publikovat.</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-white/10 px-4 py-3"><div className="text-2xl font-bold">{posts.length}</div><div className="text-[10px] uppercase tracking-wide text-slate-400">sloty</div></div>
              <div className="rounded-xl bg-white/10 px-4 py-3"><div className="text-2xl font-bold text-lime-300">{readyCount}</div><div className="text-[10px] uppercase tracking-wide text-slate-400">připraveno</div></div>
              <div className="rounded-xl bg-white/10 px-4 py-3"><div className="text-2xl font-bold text-blue-300">{postedCount}</div><div className="text-[10px] uppercase tracking-wide text-slate-400">publikováno</div></div>
            </div>
          </div>
        </section>

        <nav className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-white p-1.5 shadow-sm">
          {tabItems.map(({ key, label, icon: Icon }) => (
            <button key={key} type="button" onClick={() => setActiveTab(key)} className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${activeTab === key ? 'bg-violet-100 text-violet-800' : 'text-slate-600 hover:bg-slate-50'}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </nav>

        {error && <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {error}</div>}
        {notice && <div className="fixed bottom-5 right-5 z-[70] flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-3 text-sm font-medium text-white shadow-xl"><CheckCircle2 className="h-4 w-4" /> {notice}</div>}

        {activeTab === 'today' && (
          loading ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
              <Sparkles className="mx-auto h-6 w-6 animate-pulse text-violet-700" />
              <p className="mt-3 text-sm text-slate-500">Hledám dnešní nejsilnější příběhy…</p>
            </div>
          ) : posts.length ? (
            <div className="space-y-4">
              {posts.map((post) => (
                <SocialPostCard key={post._id} post={post} busy={busyById[post._id]} onEdit={setSelectedPost} onCopy={copyText} onReplace={handleReplace} onStatus={handleStatus} />
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">Pro tento den se nenašli kandidáti s dostatečnými podklady a použitelnou fotografií.</div>
          )
        )}

        {activeTab === 'history' && <HistoryPanel posts={historyPosts} loading={historyLoading} />}
        {activeTab === 'settings' && settings && <SettingsPanel settings={settings} onSave={handleSettingsSave} />}
      </div>

      {selectedPost && (
        <SocialPostEditor
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
          onCopy={copyText}
          onSaved={(updated) => {
            updatePost(updated)
            setSelectedPost(null)
            setNotice('Návrh uložen')
          }}
        />
      )}
    </Layout>
  )
}

export default SocialStudio
